import { useEffect } from 'react';

/**
 * Powers the premium "spotlight + tilt" micro-interaction on every
 * .ui-card / .diff-card / .form-panel in the app.
 *
 * Implementation notes (why this is one listener, not one-per-card):
 * - A single delegated `pointermove` listener on the document computes the
 *   cursor position relative to whichever card it's over and writes it to
 *   two CSS custom properties (--mx, --my) plus a rotation pair
 *   (--rx, --ry). The actual glow and tilt are pure CSS (see App.css),
 *   so the browser compositor handles the paint — no React re-renders,
 *   no per-card listeners, and it stays under the 100ms input-response
 *   budget even with a dozen cards on screen.
 * - Respects prefers-reduced-motion: the listener never attaches for
 *   people who've asked for less motion, so cards fall back to the plain
 *   hover state already defined in CSS.
 */
const TILT_SELECTOR = '.ui-card, .diff-card, .tilt-panel';
const MAX_TILT_DEG = 4; // subtle — premium, not gaming-UI

const useCardTilt = () => {
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (prefersReducedMotion) return undefined;

    let activeCard = null;

    const handleMove = (e) => {
      const card = e.target.closest(TILT_SELECTOR);

      if (card !== activeCard) {
        if (activeCard) activeCard.classList.remove('is-tilting');
        activeCard = card;
        if (activeCard) activeCard.classList.add('is-tilting');
      }
      if (!card) return;

      const rect = card.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width; // 0–1
      const py = (e.clientY - rect.top) / rect.height; // 0–1

      const rx = (0.5 - py) * MAX_TILT_DEG;
      const ry = (px - 0.5) * MAX_TILT_DEG;

      card.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
      card.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
      card.style.setProperty('--rx', `${rx.toFixed(2)}deg`);
      card.style.setProperty('--ry', `${ry.toFixed(2)}deg`);
    };

    const handleLeave = (e) => {
      const card = e.target.closest(TILT_SELECTOR);
      if (card) {
        card.classList.remove('is-tilting');
        card.style.removeProperty('--rx');
        card.style.removeProperty('--ry');
      }
      if (card === activeCard) activeCard = null;
    };

    document.addEventListener('pointermove', handleMove, { passive: true });
    document.addEventListener('pointerout', handleLeave, { passive: true });

    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerout', handleLeave);
    };
  }, []);
};

export default useCardTilt;
